import { DOCUMENT } from '@angular/common';
import { Component, ElementRef, Inject, OnDestroy, ViewChild, Renderer2 } from '@angular/core';
import { Router, NavigationStart } from '@angular/router';
import { select, Store } from '@ngrx/store';
import {
  AuthService,
  CoreUiPartialState,
  loadBrandDefaults,
  MessageService,
  getBrandDefaults,
  isExternalUser,
  getLoginDetails,
  ConfigService,
  getUserRole,
  SpinnerService,
} from '@pos-app/core-ui';
import { Observable, Subject } from 'rxjs';
import { takeUntil, filter } from 'rxjs/operators';
import { environment } from '../environments/environment';
import { GetUserStateAction } from './features/customers/+state/customers.actions';
import { CustomersPartialState } from './features/customers/+state/customers.reducer';
import { getCustomerName } from './features/customers/+state/customers.selectors';
import { LayoutService } from '@pos-app/core-ui/lib/services/layout.service';
import { MatDrawer } from '@angular/material/sidenav';

@Component({
    selector: 'pos-app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false
})
export class AppComponent implements OnDestroy {
  @ViewChild('drawer') drawer: MatDrawer;
  public runningEnv = environment.production ? 'production' : 'local';
  public unSubscribe$ = new Subject<void>();
  public isRedirecting = true;
  public isFooterHidden$ = this.layoutService.getIsFooterHidden();
  public isNavBarHidden$ = this.layoutService.getIsNavBarHidden();
  drawerStatus = false;
  role$: Observable<string>;

  constructor(
    private authService: AuthService,
    private config: ConfigService,
    private messageService: MessageService,
    private coreUiStore: Store<CoreUiPartialState>,
    private customerStore: Store<CustomersPartialState>,
    private layoutService: LayoutService,
    @Inject(DOCUMENT) private document: Document,
    private renderer: Renderer2,
    private router: Router,
    private spinnerService: SpinnerService
  ) {
    if (window.location.pathname === '/') {
      window.location.href = '/login';
    } else {
      this.isRedirecting = false;
      this.authService.restoreSession();
      this.loadClientTheme();
    }

    this.authService.restoreSession();
    this.coreUiStore.dispatch(loadBrandDefaults());

    this.coreUiStore
      .select(getBrandDefaults)
      .pipe(takeUntil(this.unSubscribe$))
      .subscribe((data) => {
        localStorage.setItem('PREDEFINED_LOCALE', JSON.stringify(data?.internalLocale));
      });
    this.coreUiStore
      .select(isExternalUser)
      .pipe(takeUntil(this.unSubscribe$))
      .subscribe((data) => {
        localStorage.setItem('IS_EXTERNAL_USER', JSON.stringify(data));
      });
    this.coreUiStore
      .select(getLoginDetails)
      .pipe(takeUntil(this.unSubscribe$))
      .subscribe((data) => {
        localStorage.setItem('IS_SHOW', JSON.stringify(data?.ShowCodes.length > 0 && data?.ShowLocation === 'SHOW'));
      });
    this.role$ = this.coreUiStore.pipe(select(getUserRole));

    // Safety: reset spinner on navigation start to avoid stuck global spinner
    this.router.events.pipe(filter((e) => e instanceof NavigationStart), takeUntil(this.unSubscribe$)).subscribe(() => {
      try {
        this.spinnerService.reset();
      } catch (e) {
        // ignore
      }
    });
  }
  public ngOnDestroy() {
    this.unSubscribe$.next();
    this.unSubscribe$.complete();
    this.messageService.ngOnDestroy();
  }

  public loadClientTheme(): void {
    let themeCssUrl = this.config.AppConfig.enableTheme ? this.config.AppConfig.themeCssUrl : '/assets/styles/arb_styles.css';
    let favicon = this.config.AppConfig.enableTheme ? this.config.AppConfig.favicon : '/favicon.ico';
    const head = this.document.getElementsByTagName('head')[0];
    let themeLink = this.document.getElementById('client-theme') as HTMLLinkElement;

    if (themeLink) {
      this.renderer.setAttribute(themeLink, 'href', themeCssUrl);
    } else {
      const style = this.document.createElement('link');
      style.id = 'client-theme';
      style.rel = 'stylesheet';
      this.renderer.setAttribute(style, 'href', `${themeCssUrl}`);
      head.appendChild(style);
    }
    let faviconLink = this.document.getElementById('favicon') as HTMLLinkElement;
    this.renderer.setAttribute(faviconLink, 'href', favicon);
  }
  drawerToggle() {
    this.drawer.toggle();
    this.drawerStatus = !this.drawerStatus;
  }
}

